import React, { useEffect, useState } from "react";
import axios from "../../http"; // axios instance that attaches the JWT (the API now enforces RBAC)
import { toast } from "react-toastify";
import { MASTER_API } from "../../apiConfig";
import Layout from "../../Component/Layout";
import Select from "react-select";
import MaskedValue from "../../Component/MaskedValue";

// ── Design tokens matched to Product Master (same fonts/colors/spacing) ──
const smallInput = { fontSize: "12px", padding: "5px 8px", height: "30px", borderRadius: "6px", border: "1px solid #cbd5e1", color: "#1e293b", boxSizing: "border-box" };
const smallLabel = { fontSize: "11px", fontWeight: 700, color: "#0f172a", marginBottom: "4px", display: "block" };
const thStyle = { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "10px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", textAlign: "left", whiteSpace: "normal", wordBreak: "break-word" };
const tdStyle = { fontSize: 13, padding: "10px", borderBottom: "1px solid #f1f5f9", color: "#334155", whiteSpace: "normal", wordBreak: "break-word" };

const styles = {
  pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  pageTitle: { fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 },
  pageSubtitle: { fontSize: 12, color: '#64748b', margin: 0 },
  filterCard: { background: '#ffffff', borderRadius: 8, padding: 15, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 15 },
  tableContainer: { background: "#ffffff", borderRadius: 8, padding: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
  compactLabel: { fontSize: "12px", fontWeight: 600, marginBottom: "2px", color: '#334155' },
};

// ── Styles for the Construction..Voltage cable-attribute filter row (matches Production Order) ──
const cableFilterSelectStyles = {
  control: (base, state) => ({ ...base, minHeight: 32, height: 32, fontSize: 13, borderColor: state.isFocused ? "#64748b" : "#dee2e6", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#b3b3b3" } }),
  valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32, flexWrap: "nowrap" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 13 }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", svg: { width: 12, height: 12 } }),
  clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", svg: { width: 12, height: 12 } }),
  indicatorSeparator: () => ({ display: "none" }),
  placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 13, whiteSpace: "nowrap" }),
  singleValue: (base) => ({ ...base, fontSize: 13, color: "#1e293b" }),
  menu: (base) => ({ ...base, zIndex: 9999, fontSize: 13, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({ ...base, fontSize: 13, padding: "6px 10px", backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
};

// ── Helper: convert config API response to {value, label} options (same as Production Order) ──
const formatQty = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const num = parseFloat(val);
  if (isNaN(num)) return val;

  // Whole number (no decimal part) -> always show 2 decimals, e.g. 1000.00
  if (Number.isInteger(num)) {
    return num.toFixed(2);
  }

  // Has a decimal part -> show up to 4 decimals, trimming trailing zeros
  let str = num.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
  const [whole, dec = ""] = str.split(".");
  return `${whole}.${dec.padEnd(2, "0")}`;
};

// const getPoFilterOptions = (list) => {
//   if (!list || !Array.isArray(list)) return [];
//   return list.map((item) => {
//     if (typeof item === "string") return { value: item, label: item };
//     const val = item.name || item.code || item.cable_type || item.category
//       || item.core || item.colour || item.value || "";
//     return { value: String(val).trim(), label: String(val).trim() };
//   }).filter((opt) => opt.value !== "");
// };

const emptyComponent = () => ({
  level: "",
  item: "",
  product_type: "",
  component_product: "",
  component_description: "",
  quantity: "",
  uom_component: "",
  assembly: "",
  valid_from: "",
  valid_to: ""
});

/* ======================================================================
   RBAC  (module id MD_BOM — must match TILE_REGISTRY / FIELD_REGISTRY)
   Access types (from Create Role):
     CREATE  -> can add BOMs, manage and publish drafts
                (CREATE field list = fields turned off on the form)
     DISPLAY -> can view BOMs and export
                (DISPLAY field list = values that are masked)
     CHANGE  -> can view + edit/delete, replace deleted BOMs on publish
                (CHANGE field list = fields that are read-only)
   Admin / Full Access users bypass every check.
   Fails closed: until permissions load nobody gets access.
   The backend enforces the same rules; this only drives the UI.
   ====================================================================== */
const useBomAccess = () => {
  const [state, setState] = useState({
    loading: true, error: null, isAdmin: false, types: [],
    off: { DISPLAY: [], CHANGE: [], CREATE: [] } // type -> field ids turned off
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${MASTER_API}/bom/my-access`);
        const a = res.data || {};
        if (!cancelled) {
          setState({
            loading: false,
            error: null,
            isAdmin: !!a.admin,
            types: (a.types || []).map((x) => String(x).toUpperCase()),
            off: {
              DISPLAY: a.off?.DISPLAY || [],
              CHANGE: a.off?.CHANGE || [],
              CREATE: a.off?.CREATE || []
            }
          });
        }
      } catch (e) {
        console.error("[BOM RBAC] could not load /bom/my-access", e);
        const why = e.response
          ? `${e.response.status} ${e.response.data?.message || e.response.statusText || ""}`
          : (e.message || "network error");
        if (!cancelled) setState((st) => ({ ...st, loading: false, isAdmin: false, error: why }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const has = (...t) => state.isAdmin || t.some((x) => state.types.includes(x));

  // state of one field for the current form mode
  //  create             -> hidden when the CREATE list turns it off
  //  view / view_draft  -> masked when the DISPLAY list turns it off
  //  edit               -> masked if DISPLAY hides it, read-only if CHANGE turns it off
  const fieldState = (field, mode) => {
    if (state.isAdmin) return { hidden: false, masked: false, readOnly: false };
    const off = (type) => state.off[type].includes(field);
    if (mode === "create") return { hidden: off("CREATE"), masked: false, readOnly: false };
    if (mode === "view" || mode === "view_draft") return { hidden: false, masked: off("DISPLAY"), readOnly: false };
    if (mode === "edit") return { hidden: false, masked: off("DISPLAY"), readOnly: off("CHANGE") };
    return { hidden: false, masked: false, readOnly: false };
  };

  return {
    loading: state.loading,
    error: state.error,
    isAdmin: state.isAdmin,
    canView: has("DISPLAY", "CHANGE", "REVIEW", "CONVERT"),
    canCreate: has("CREATE"),
    canEdit: has("CHANGE"),
    fieldState
  };
};

// One labelled header field: hidden / masked / read-only according to its state.
const FieldBox = ({ st, label, children }) => {
  if (st.hidden) return null;
  return (
    <div>
      <label style={smallLabel}>{label}</label>
      {st.masked
        ? <MaskedValue />
        : <div style={st.readOnly ? { pointerEvents: "none", opacity: 0.65 } : undefined}>{children}</div>}
    </div>
  );
};

const BOM = () => {

  const access = useBomAccess();
  const canView = access.canView;
  const canCreate = access.canCreate;
  const canEdit = access.canEdit;
  // Publishing a draft is part of creating BOMs (backend: CREATE, plus CHANGE when replacing).
  const canPublish = access.canCreate;
  // Row edits inside the BOM form: Create while adding, Change while editing.
  const canEditRows = access.canCreate || access.canEdit;
  const [expandedNodes, setExpandedNodes] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [referenceList, setReferenceList] = useState([]);
  const [referenceBom, setReferenceBom] = useState("");

    const [showBulkModal, setShowBulkModal] = useState(false);
  const [showMasterPreview, setShowMasterPreview] = useState(false);
  const [draftsList, setDraftsList] = useState([]);
  const [draftsCount, setDraftsCount] = useState(0);
  const [missingProducts, setMissingProducts] = useState([]);
  const [showMissingModal, setShowMissingModal] = useState(false);

  // ── Deleted-BOM replace confirmation (mirrors Product Master) ──
  const [deletedConflictCodes, setDeletedConflictCodes] = useState([]);
  const [showDraftReplaceConfirm, setShowDraftReplaceConfirm] = useState(false);
  const [draftReplaceTarget, setDraftReplaceTarget] = useState(null);
  const [showBulkReplaceConfirm, setShowBulkReplaceConfirm] = useState(false);
  const [isPublishingAll, setIsPublishingAll] = useState(false);
  const [publishingId, setPublishingId] = useState(null);

  const [bomList, setBomList] = useState([]);
  //eslint-disable-next-line
  const [details, setDetails] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showTreeModal, setShowTreeModal] = useState(false);
  const [reopenTreeAfterView, setReopenTreeAfterView] = useState(false);
const [bomTree, setBomTree] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bomToDelete, setBomToDelete] = useState(null);

  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  //eslint-disable-next-line
  const [uomList, setUomList] = useState([]);
  const [productTypeOptions, setProductTypeOptions] = useState([]);
  const [processList, setProcessList] = useState([]);
  const [activeTab, setActiveTab] = useState("component");
  const [documents, setDocuments] = useState([]);
  const [bomLogs, setBomLogs] = useState([]);
  const [deletingDraftCode, setDeletingDraftCode] = useState(null);

  const [form, setForm] = useState({
    product_code: "",
    process_id: "",
    process_name: "",
    product_description: "",
    base_unit: "",
    UOM: "",
    location: "",
    bom_usage: "",
    alternative_bom: "",
  });

  const equalBtnStyle = {
    width: "200px", height: "38px", display: "flex", alignItems: "center", justifyContent: "center",
    gap: "6px", fontWeight: 600, borderRadius: "6px", whiteSpace: "nowrap", background: "#021843",
    color: "#fff", border: "1px solid #021843", outline: "none", boxShadow: "none",
    cursor: "pointer", transition: "none"
  };

  const [componentRows, setComponentRows] = useState([emptyComponent()]);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const [filters, setFilters] = useState({
    productType: null,
    construction: null,
    code: null,
    cableType: null,
    category: null,
    noOfCores: null,
    color: null,
    nominalCrossSection: null,
    referenceOfStandard: null,
    voltage: null,
    productCode: null,
    productDescription: null,
    process: null
  });

  useEffect(() => {
    loadBOMList();
    loadDrafts();
    loadMissingProducts();

    axios.get(`${MASTER_API}/bom/products`).then((res) => setProducts(res.data.data || [])).catch((err) => { console.error("Failed loading products for BOM filters", err.response?.data || err.message); });
    axios.get(`${MASTER_API}/bom/reference/list`).then(res => setReferenceList(res.data.data || [])).catch(() => { });
    axios.get(`${MASTER_API}/configuration/location`).then((res) => setLocations(res.data || [])).catch(() => { });
    axios.get(`${MASTER_API}/configuration/uom`).then((res) => setUomList(res.data || [])).catch(() => { });
    axios.get(`${MASTER_API}/configuration/product-types`).then((res) => {
      const raw = res.data || [];
      const opts = raw
        .map((r) => String(r.name || r.value || "").trim())
        .filter((v) => v !== "")
        .map((v) => ({ value: v, label: v }));
      setProductTypeOptions(opts);
    }).catch((err) => { console.error("Failed loading product types for BOM filter", err.response?.data || err.message); });
    axios.get(`${MASTER_API}/bom/processes`).then((res) => setProcessList(res.data.data || [])).catch((err) => { console.error("Failed loading processes for BOM", err.response?.data || err.message); });
  }, []);

  // ── Product Type options come straight from the configuration master list ──
  // ── (Configuration → Master Data → Product Type) — all entries are shown, not ──
  // ── just Finished Goods / Semi Finished — and this avoids duplicate/inconsistently- ──
  // ── cased entries like "Finished Goods" vs "FINISHED GOODS" that could exist in the ──
  // ── raw product data. ──
  const bomProductTypeOptions = productTypeOptions;

  // ── All listing-page filter keys that participate in cascading (Product Type ──
  // ── through Voltage, plus Product Code / Product Description) ──
  const ATTRIBUTE_FILTER_KEYS = [
    "productType", "construction", "code", "cableType", "category",
    "noOfCores", "color", "nominalCrossSection", "referenceOfStandard",
    "voltage", "productCode", "productDescription"
  ];

  // Only filters by a key if that field is actually present on the product records —
  // this prevents a missing/renamed backend column from silently zeroing out all results.
  const productKeys = products.length > 0 ? Object.keys(products[0]) : [];

  const productMatchesFilterSet = (p, filterSet, excludeKey) =>
    Object.entries(filterSet).every(([key, val]) => {
      if (key === excludeKey) return true;
      if (!val) return true;
      if (!productKeys.includes(key)) return true;
      const productVal = String(p[key] || "").trim().toLowerCase();
      const filterVal = String(val.value || "").trim().toLowerCase();
      return productVal === filterVal;
    });

  // Changing one filter narrows every other filter's options. If the newly picked
  // combination no longer matches any product, the least-recently-touched of the
  // OTHER active filters are cleared (one at a time) until the combination is valid
  // again — this keeps all the filters interconnected instead of showing 0 results.
  const handleFilterChange = (key, opt) => {
    setFilters((prev) => {
      let updated = { ...prev, [key]: opt };
      if (products.length === 0) return updated;

      const clearOrder = ATTRIBUTE_FILTER_KEYS.filter((k) => k !== key).reverse();
      let i = 0;
      while (
        i < clearOrder.length &&
        !products.some((p) => productMatchesFilterSet(p, updated, null))
      ) {
        updated = { ...updated, [clearOrder[i]]: null };
        i++;
      }

      // Product Code <-> Product Description auto-fill: picking one fills in
      // the other whenever the pairing is unambiguous.
      if (key === "productCode") {
        if (opt) {
          const match = products.find((p) => String(p.productCode || "").trim() === String(opt.value).trim());
          const desc = match && match.productDescription ? String(match.productDescription).trim() : "";
          updated = { ...updated, productDescription: desc ? { value: desc, label: desc } : null };
        } else {
          updated = { ...updated, productDescription: null };
        }
      } else if (key === "productDescription") {
        if (opt) {
          const matches = products.filter((p) => String(p.productDescription || "").trim() === String(opt.value).trim());
          updated = { ...updated, productCode: matches.length === 1 ? { value: String(matches[0].productCode || "").trim(), label: String(matches[0].productCode || "").trim() } : updated.productCode };
        } else {
          updated = { ...updated, productCode: null };
        }
      }

      return updated;
    });
    setCurrentPage(1);
  };

  // ── Resets every listing-page filter back to empty in one click ──
  const clearAllFilters = () => {
    setFilters({
      productType: null,
      construction: null,
      code: null,
      cableType: null,
      category: null,
      noOfCores: null,
      color: null,
      nominalCrossSection: null,
      referenceOfStandard: null,
      voltage: null,
      productCode: null,
      productDescription: null,
      process: null
    });
    setCurrentPage(1);
  };

  const hasActiveFilters = Object.values(filters).some((v) => !!v);

  // ── Products narrowed down by ALL currently active filters ──
  const filteredProducts = products.filter((p) => productMatchesFilterSet(p, filters, null));

  // ── Cascading options for a given filter field: computed from products that match ──
  // ── every OTHER active filter (so a field's own current selection never hides its ──
  // ── sibling options), restricted to values that actually appear in the data. ──
  const getCascadingOptions = (key) => {
    if (!productKeys.includes(key)) return [];
    const relevant = products.filter((p) => productMatchesFilterSet(p, filters, key));
    const uniqueVals = Array.from(
      new Set(
        relevant
          .map((p) => p[key])
          .filter((v) => v !== undefined && v !== null && v !== "")
          .map((v) => String(v).trim())
          .filter((v) => v !== "")
      )
    );
    return uniqueVals
      .map((v) => ({ value: v, label: v }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  // Product Code / Product Description options are further restricted to codes that
  // actually exist in the BOM table, since those are the only ones the table can show.
  const bomProductCodesInTable = new Set(bomList.map((item) => item.product_code));
  const bomProductDescInTable = new Set(bomList.map((item) => item.product_description));

  const bomProductCodeOptions = getCascadingOptions("productCode").filter((o) => bomProductCodesInTable.has(o.value));
  const bomProductDescOptions = getCascadingOptions("productDescription").filter((o) => bomProductDescInTable.has(o.value));

  // ── Process filter options — restricted to process IDs that actually appear ──
  // ── in the BOM table (so the dropdown never offers a process with 0 results) ──
  const bomProcessIdsInTable = new Set(bomList.map((item) => String(item.process_id || "").trim()).filter((v) => v !== ""));
  const bomProcessOptions = processList
    .filter((p) => bomProcessIdsInTable.has(String(p.process_id || "").trim()))
    .map((p) => ({ value: p.process_id, label: `${p.process_id} - ${p.process_name || ""}` }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleProcessFilterChange = (opt) => {
    setFilters((prev) => ({ ...prev, process: opt }));
    setCurrentPage(1);
  };

  const hasAttributeFiltersActive = ATTRIBUTE_FILTER_KEYS.some((k) => filters[k]);

  const filteredBomList = bomList.filter((item) => {
    if (filters.process && String(item.process_id || "").trim() !== String(filters.process.value || "").trim()) return false;
    if (!hasAttributeFiltersActive) return true;
    return filteredProducts.some((p) => p.productCode === item.product_code);
  });

  const totalPages = Math.ceil(filteredBomList.length / recordsPerPage);
  const paginatedBomList = filteredBomList.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);

  const handleExportBOM = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/bom/export`, {
        responseType: "blob"
      });

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `BOM_Export_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("BOM data exported successfully");
    } catch (err) {
      console.error("EXPORT ERROR:", err.response?.data || err);
      toast.error("Failed to export BOM data");
    }
  };

  const handleExportFinishedGoodsTree = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/bom/export-tree`, {
        responseType: "blob"
      });

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Finished_Goods_BOM_Tree_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Finished Goods BOM tree exported successfully");
    } catch (err) {
      console.error("EXPORT TREE ERROR:", err.response?.data || err);
      toast.error("Failed to export Finished Goods BOM tree");
    }
  };

  const loadBOMList = () => {
    axios.get(`${MASTER_API}/bom`).then((res) => setBomList(res.data.data || [])).catch(() => { setBomList([]); });
  };

    const loadDrafts = () => {
    axios.get(`${MASTER_API}/bom/get-drafts`)
      .then(res => {
        if (res.data.success) {
          const drafts = res.data.drafts || [];
          setDraftsList(drafts);
          setDraftsCount(drafts.length);
          setDeletedConflictCodes(res.data.deletedConflicts || []);
        }
      })
      .catch(err => console.error("Failed to load drafts", err));
  };

  const loadMissingProducts = () => {
  axios.get(`${MASTER_API}/bom/missing-products`)
    .then(res => setMissingProducts(res.data.data || []))
    .catch(err => console.error("Failed to load missing products", err));
};

  const openBomTree = async (productCode) => {
  try {
    const res = await axios.get(
      `${MASTER_API}/bom/tree/${productCode}`
    );

    setBomTree(res.data.data);
    setExpandedNodes({});
    setShowTreeModal(true);
  } catch (err) {
    toast.error("Failed to load BOM tree");
  }
};

const nodeKey = (node, path) => `${path}/${node.code}`;

const collectNodeKeys = (node, path = "", acc = []) => {
  if (!node) return acc;
  const key = nodeKey(node, path);
  if (node.children?.length) {
    acc.push(key);
    node.children.forEach((c) => collectNodeKeys(c, key, acc));
  }
  return acc;
};

const expandAllNodes = () => {
  const all = {};
  collectNodeKeys(bomTree).forEach((k) => (all[k] = true));
  setExpandedNodes(all);
};

const collapseAllNodes = () => {
  const all = {};
  collectNodeKeys(bomTree).forEach((k) => (all[k] = false));
  // keep the root open so the first level is always visible
  const rootKey = nodeKey(bomTree, "");
  all[rootKey] = true;
  setExpandedNodes(all);
};

// Compact, single-line tree row (same look & feel as Lead Time schedule tree)
const renderBomNode = (node, level = 0, path = "") => {
  if (!node) return null;

  const key = nodeKey(node, path);
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes[key] ?? level < 1; // only root open by default

  const hasQty = node.quantity !== undefined && node.quantity !== null && node.quantity !== "";
  const meta = [
    hasQty ? `Qty: ${node.quantity}` : null,
    node.base_unit ? `Base: ${node.base_unit}` : null,
    node.uom ? `UOM: ${node.uom}` : null
  ].filter(Boolean).join("  •  ");

  return (
    <div key={key}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "6px 12px",
          marginLeft: level * 25,
          borderRadius: 6,
          marginBottom: 4,
          background: level === 0 ? "#e3f2fd" : "white",
          border: "1px solid #eee",
          fontWeight: level === 0 ? 700 : 500,
          fontSize: 13,
          cursor: hasChildren ? "pointer" : "default"
        }}
        onClick={hasChildren ? () => toggleNode(key, level) : undefined}
      >
        {hasChildren ? (
          <span style={{ marginRight: 8, fontSize: 12, width: 12 }}>
            {isExpanded ? "▼" : "▶"}
          </span>
        ) : (
          <span style={{ marginRight: 8, width: 12 }} />
        )}

        {/* Code (clickable when a BOM exists) */}
        <div
          style={{
            minWidth: 150,
            color: node.bom_id ? "#2563eb" : undefined,
            textDecoration: node.bom_id ? "underline" : "none",
            cursor: node.bom_id ? "pointer" : "inherit",
            whiteSpace: "nowrap"
          }}
          title={node.bom_id ? "View BOM for this product" : undefined}
          onClick={
            node.bom_id
              ? (e) => {
                  e.stopPropagation();
                  setReopenTreeAfterView(true);
                  setShowTreeModal(false);
                  viewDetails(node.bom_id);
                }
              : undefined
          }
        >
          {node.code}
        </div>

        {/* Description (one line, truncated) */}
        <div
          style={{
            flex: 1,
            fontSize: 12,
            fontWeight: 400,
            color: "#64748b",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            paddingRight: 12
          }}
          title={node.description || ""}
        >
          {node.description || "No Description"}
        </div>

        {/* Qty / Base unit / UOM */}
        {meta && (
          <div style={{ fontSize: 12, fontWeight: 400, color: "#555", whiteSpace: "nowrap" }}>
            {meta}
          </div>
        )}

        {hasChildren && (
          <span
            style={{
              marginLeft: 12,
              background: "#e0f2fe",
              padding: "2px 8px",
              borderRadius: 20,
              fontSize: 10,
              fontWeight: 600,
              whiteSpace: "nowrap"
            }}
          >
            {node.children.length} items
          </span>
        )}
      </div>

      {isExpanded &&
        hasChildren &&
        node.children.map((child) => renderBomNode(child, level + 1, key))}
    </div>
  );
};

  const handleBulkExcelUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${MASTER_API}/bom/parse-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

            if (response.data.success) {
        setShowBulkModal(false);
        loadDrafts();
        loadMissingProducts(); 
        setShowMasterPreview(true);

        // alert about missing products while it's in draft
        if (response.data.missingProductsCount > 0) {
          toast.warn(`${response.data.missingProductsCount} product(s) in this upload are not maintained in Product Master.`);
        }

        // alert about products that match a previously deleted BOM
        if (response.data.deletedConflicts?.length > 0) {
          setDeletedConflictCodes(response.data.deletedConflicts);
          toast.info(
            `${response.data.deletedConflicts.length} BOM(s) match previously deleted entries. ` +
            `You'll be asked to confirm when publishing them.`
          );
        }
      }
    } catch (error) {
      toast.error('Failed to parse the file. Ensure the template is correct.');
    }
    event.target.value = null;
  };
const toggleNode = (key, level = 1) => {
  setExpandedNodes((prev) => {
    const current = prev[key] ?? level < 1;
    return { ...prev, [key]: !current };
  });
};
    const publishAllDraftsApi = async (replaceExisting) => {
    if (!canPublish) {
      toast.error("You don't have Create access.Please contact admin.");
      return;
    }
    setIsPublishingAll(true);
    try {
      const res = await axios.post(`${MASTER_API}/bom/publish-all-drafts`, { replaceExisting });
      if (res.data.success !== false) {
        toast.success(res.data.message || "All BOMs Saved to Main Table Successfully!");
        setDeletedConflictCodes([]); // all handled now
        loadDrafts();
        loadMissingProducts();
        loadBOMList();
        setShowMasterPreview(false);
      } else {
        toast.error(res.data.message || "Failed to save BOMs.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save BOMs.");
    } finally {
      setIsPublishingAll(false);
    }
  };

  const handlePublishAllDrafts = () => {
    if (!canPublish) {
      toast.error("You don't have Create access.Please contact admin.");
      return;
    }
    if (draftsList.length === 0) return;

    const conflictsInList = draftsList
      .map(d => d.parentCode)
      .filter(code => deletedConflictCodes.includes(code));

    if (conflictsInList.length > 0) {
      setShowBulkReplaceConfirm(true);
      return;
    }

    publishAllDraftsApi(false);
  };

  const handlePublishDraft = async (parentCode, replaceExisting = false) => {
    if (!canPublish) {
      toast.error("You don't have Create access.Please contact admin.");
      return;
    }
    setPublishingId(parentCode);
    try {
      const res = await axios.post(`${MASTER_API}/bom/publish-draft`, { parentCode, replaceExisting });
      toast.success(res.data.message || `BOM for ${parentCode} Saved Successfully!`);
      setDeletedConflictCodes(prev => prev.filter(c => c !== parentCode));
      loadDrafts();
      loadMissingProducts();
      loadBOMList();
      if (draftsList.length <= 1) setShowMasterPreview(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save BOM.");
    } finally {
      setPublishingId(null);
    }
  };

  const onClickPublishDraft = (parentCode) => {
    if (deletedConflictCodes.includes(parentCode)) {
      setDraftReplaceTarget(parentCode);
      setShowDraftReplaceConfirm(true);
    } else {
      handlePublishDraft(parentCode, false);
    }
  };

  const handleDeleteDraft = async (parentCode) => {
  if (!canCreate) return toast.error("You don't have Create access. Please contact admin.");
  const confirmed = window.confirm(`Delete draft for ${parentCode}? This cannot be undone.`);
  if (!confirmed) return;

  setDeletingDraftCode(parentCode);
  try {
    await axios.delete(`${MASTER_API}/bom/drafts/${parentCode}`);
    toast.success(`Draft for ${parentCode} deleted`);
    loadDrafts();
    loadMissingProducts();
  } catch (err) {
    console.error("Failed to delete draft", err);
    toast.error(err.response?.data?.message || "Failed to delete draft");
  } finally {
    setDeletingDraftCode(null);
  }
};

  const viewPreviewDetails = (bom) => {
    setForm({
      product_code: bom.parentCode || "",
      product_description: bom.headerDetails.product_description || "",
      base_unit: bom.headerDetails.baseUnit || "",
      UOM: bom.headerDetails.UOM || "",
      location: bom.headerDetails.location || "",
      bom_usage: bom.headerDetails.bomUsage || "",
      alternative_bom: bom.headerDetails.alternativeBom || "",
    });

    if (bom.components && bom.components.length > 0) {
      setComponentRows(bom.components.map((c) => ({
        level: c.level,
        item: c.item,
        product_type: c.product_type,
        component_product: c.component_product,
        component_description: c.component_description,
        quantity: formatQty(c.quantity),
        uom_component: c.uom_component,
        assembly: c.assembly,
        valid_from: c.valid_from ? c.valid_from.substring(0, 10) : "",
        valid_to: c.valid_to ? c.valid_to.substring(0, 10) : "",
      })));
    } else {
      setComponentRows([emptyComponent()]);
    }

    setActiveTab("component");
    setShowModal(true);
  };

  const handleDocumentUpload = (e) => setDocuments([...documents, ...e.target.files]);

  const handleDeleteBom = (bom_id) => {
    if (!canEdit) return toast.error("You don't have Change access. Please contact admin.");
    setBomToDelete(bom_id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteBom = async () => {
    const bom_id = bomToDelete;
    setShowDeleteConfirm(false);

    if (!bom_id) return;

    try {
      await axios.delete(`${MASTER_API}/bom/delete/${bom_id}`);

      toast.success("BOM deleted successfully");

      // remove from UI immediately, DB keeps it with status = 'deleted'
      setBomList((prev) => prev.filter((b) => b.bom_id !== bom_id));

    } catch (e) {
      console.error("Failed to delete BOM", e);
      toast.error("Failed to delete BOM");
    } finally {
      setBomToDelete(null);
    }
  };

  const viewDetails = async (bom_id) => {
    if (!canView) {
      toast.error("You don't have View access. Please contact admin.");
      return;
    }
    try {
      setDetails(null);
      setBomLogs([]);
      setEditingId(bom_id);
      setIsEditing(false);

      const res = await axios.get(`${MASTER_API}/bom/${bom_id}`);
      const logRes = await axios.get(`${MASTER_API}/bom/change-logs/${bom_id}`);

      setBomLogs(logRes.data || []);
      const data = res.data.data || { header: null, components: [] };
      setDetails(data);
      setForm({
        product_code: data.header.product_code || "",
        process_id: data.header.process_id || "",
        process_name: data.header.process_name || "",
        product_description: data.header.product_description || "",
        base_unit: data.header.base_unit || "",
        UOM: data.header.UOM || "",
        location: data.header.location || "",
        bom_usage: data.header.bom_usage || "",
        alternative_bom: data.header.alternative_bom || "",
      });
      if (data.components.length > 0) {
        setComponentRows(data.components.map((c) => ({
          level: c.level,
          item: c.item,
          product_type: c.product_type,
          component_product: c.component_product,
          component_description: c.component_description,
          quantity: formatQty(c.quantity),
          uom_component: c.uom_component,
          assembly: c.assembly,
          valid_from: c.valid_from ? c.valid_from.substring(0, 10) : "",
          valid_to: c.valid_to ? c.valid_to.substring(0, 10) : "",
        })));
      } else { setComponentRows([emptyComponent()]); }
      setShowModal(true);
    } catch (err) { toast.error("Failed to load BOM details"); }
  };

  const closeViewModal = () => {
    setShowModal(false);
    setEditingId(null);
    setBomLogs([]);
    if (reopenTreeAfterView) {
      setReopenTreeAfterView(false);
      setShowTreeModal(true);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({
      product_code: "", process_id: "", process_name: "", product_description: "", base_unit: "", UOM: "",
      location: "", bom_usage: "", alternative_bom: ""
    });
    setComponentRows([emptyComponent()]);
    setBomLogs([]);
    setDocuments([]); setActiveTab("component"); setShowModal(true);
  };

  const addComponentRow = () => {
    if (!canEditRows) return toast.error("You don't have Edit access.Please contact admin.");
    setComponentRows((r) => [...r, { ...emptyComponent() }]);
  };

  const removeComponentRow = (idx) => {
    if (!canEditRows) return toast.error("You don't have Edit access.Please contact admin.");
    setComponentRows((r) => r.filter((_, i) => i !== idx));
  };

  const updateComponentRow = (idx, field, value) => {
    if (!canEditRows) return toast.error("You don't have Edit access.Please contact admin.");
    setComponentRows((r) => r.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  };

  const handleSave = async () => {
    if (!editingId && !canCreate) return toast.error("You don't have Create access.Please contact admin.");
    if (editingId && !canEdit) return toast.error("You don't have Edit access.Please contact admin.");
    if (!form.product_code) return toast.error("Please select product code");
    if (!form.location) return toast.error("Please select location");

    const payload = new FormData();
    payload.append("product_code", form.product_code || "");
    payload.append("process_id", form.process_id || "");
    payload.append("process_name", form.process_name || "");
    payload.append("product_description", form.product_description || "");
    payload.append("base_unit", form.base_unit || "");
    payload.append("UOM", form.UOM || "");
    payload.append("location", form.location || "");
    payload.append("bom_usage", form.bom_usage || "");
    payload.append("alternative_bom", form.alternative_bom || "");
    payload.append("reference_bom", referenceBom || "");
    payload.append("components", JSON.stringify(componentRows));
    documents.forEach((f) => payload.append("documents", f));

    try {
      if (editingId && editingId !== "preview") {
        await axios.put(`${MASTER_API}/bom/update/${editingId}`, payload, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("BOM updated successfully");
      } else {
        await axios.post(`${MASTER_API}/bom/add`, payload, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("BOM created successfully");
      }

      setShowModal(false); setEditingId(null); setBomLogs([]); loadBOMList();
      setForm({ product_code: "", process_id: "", process_name: "", base_unit: "", UOM: "", location: "", bom_usage: "", alternative_bom: "" });
      setComponentRows([emptyComponent()]); setDocuments([]);
    } catch (err) {
      console.log("SAVE ERROR:", err.response?.data || err);
      toast.error(err.response?.data?.message || "Failed to save BOM");
    }
  };

  // form mode -> field rules
  const bomMode = editingId === "preview" ? "view_draft" : !editingId ? "create" : isEditing ? "edit" : "view";
  const fs = (field, mode = bomMode) => access.fieldState(field, mode);
  // list-table cell: MaskedValue when the user's Display role hides the column
  const mv = (field, value) => (fs(field, "view").masked ? <MaskedValue height={22} /> : value);
  // component-table cell: masked / read-only according to the field state
  const cf = (field, node) => {
    const st = fs(field);
    if (st.masked) return <MaskedValue height={22} />;
    return st.readOnly ? <div style={{ pointerEvents: "none", opacity: 0.65 }}>{node}</div> : node;
  };

  if (access.loading) {
    return <Layout><div style={{ padding: 20, fontSize: 13, color: "#64748b" }}>Loading permissions…</div></Layout>;
  }
  if (!access.canView && !access.canCreate) {
    return (
      <Layout>
        <div style={{ padding: 20 }}>
          <h2 style={styles.pageTitle}>Bill of Materials</h2>
          <p style={styles.pageSubtitle}>
            {access.error
              ? `Could not load your permissions (${access.error}).`
              : "Your role doesn't include the BOM module (MD_BOM). Ask an administrator to add it to your role, then log in again."}
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: 20 }}>
        {/* HEADER SECTION */}
        <div style={styles.pageTitleBar}>
          <div>
            <h2 style={styles.pageTitle}>Bill of Materials</h2>
            <p style={styles.pageSubtitle}>Manage and view product BOMs</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {canView && (
              <button
                style={{ ...equalBtnStyle, width: "160px", height: "36px", background: "#fff", color: "#021843", border: "1px solid #021843", fontSize: "13px" }}
                onClick={handleExportBOM}
              >
                ⬇️ Export
              </button>
            )}

            {canView && (
              <button
                style={{ ...equalBtnStyle, width: "220px", height: "36px", background: "#fff", color: "#021843", border: "1px solid #021843", fontSize: "13px" }}
                onClick={handleExportFinishedGoodsTree}
              >
                ⬇️ Export Finished Goods Tree
              </button>
            )}

            <button
              style={{ ...equalBtnStyle, width: "220px", height: "36px", background: "#fff", color: "#021843", border: "1px solid #021843", fontSize: "13px" }}
              onClick={() => {
                if (!canCreate) return toast.error("You don't have Create access.Please contact admin.");
                setShowBulkModal(true);
              }}
            >
              ☁️ Create BOM By Upload
            </button>

            {missingProducts.length > 0 && (
              <button
                style={{ ...equalBtnStyle, width: "260px", height: "36px", background: "#fff", color: "#b45309", border: "1px solid #f59e0b", fontSize: "13px", position: "relative" }}
                onClick={() => setShowMissingModal(true)}
              >
                ⚠️ Products To Be Maintained
                <span style={{
                  position: "absolute", top: -8, right: -8,
                  background: "#ef4444", color: "#fff",
                  borderRadius: "50%", minWidth: 20, height: 20,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 700, padding: "0 4px"
                }}>
                  {missingProducts.length}
                </span>
              </button>
            )}

            <button
              style={{ ...equalBtnStyle, width: "160px", height: "36px" }}
              onClick={() => {
                if (!canCreate) return toast.error("You don't have Create access. Please contact admin.");
                openCreate();
              }}
            >
              + Create BOM
            </button>
          </div>
        </div>

        {/* FILTERS */}
        <div style={styles.filterCard}>

          {/* Row 1: Product Type */}
          <div className="row mb-3">
            <div className="col-md-3">
              <label style={styles.compactLabel}>Product Type</label>
              <Select
                options={bomProductTypeOptions}
                value={filters.productType}
                onChange={(opt) => handleFilterChange("productType", opt)}
                placeholder="Select Product Type"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ control: (base) => ({ ...base, minHeight: 32, height: 32, fontSize: 13, borderColor: '#dee2e6' }), menu: (base) => ({ ...base, zIndex: 9999, fontSize: 13 }), menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
            <div className="col-md-2" style={{ display: "flex", alignItems: "flex-end" }}>
              <button
                type="button"
                onClick={clearAllFilters}
                disabled={!hasActiveFilters}
                style={{
                  height: 32,
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "0 12px",
                  borderRadius: 6,
                  border: "1px solid #dee2e6",
                  background: hasActiveFilters ? "#fff" : "#f8fafc",
                  color: hasActiveFilters ? "#334155" : "#94a3b8",
                  cursor: hasActiveFilters ? "pointer" : "not-allowed",
                  whiteSpace: "nowrap"
                }}
              >
                ✕ Clear All Filters
              </button>
            </div>
          </div>

          {/* Row 2: Construction, Code, Cable Type, Category, Cores, Color, Cross Section, Std. Ref, Voltage */}
          {/* Each field's options are recomputed from products matching every OTHER active filter, so all of these cascade together. */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 18 }}>
            {[
              { key: "construction", label: "Construction" },
              { key: "code", label: "Code" },
              { key: "cableType", label: "Cable Type" },
              { key: "category", label: "Category" },
              { key: "noOfCores", label: "Cores" },
              { key: "color", label: "Color" },
              { key: "nominalCrossSection", label: "Cross Section" },
              { key: "referenceOfStandard", label: "Std. Ref" },
              { key: "voltage", label: "Voltage" },
            ].map(({ key, label }) => (
              <div key={key} style={{ flex: "1 1 0", minWidth: 100 }}>
                <label style={styles.compactLabel}>{label}</label>
                <Select
                  placeholder={label}
                  isClearable
                  options={getCascadingOptions(key)}
                  value={filters[key]}
                  onChange={(opt) => handleFilterChange(key, opt)}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={cableFilterSelectStyles}
                />
              </div>
            ))}
          </div>

          {/* Row 3: Product Code, Product Description — cascade with every filter above, and with each other — plus Process, filtered directly against the BOM table */}
          <div className="row g-3">
            <div className="col-md-4">
              <label style={styles.compactLabel}>Product Code</label>
              <Select
                isDisabled={editingId && !isEditing}
                options={bomProductCodeOptions}
                value={filters.productCode}
                onChange={(opt) => handleFilterChange("productCode", opt)}
                placeholder="Product Code..."
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ control: (base) => ({ ...base, minHeight: 32, height: 32, fontSize: 13, borderColor: '#dee2e6' }), menu: (base) => ({ ...base, zIndex: 9999, fontSize: 13 }), menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
            <div className="col-md-4">
              <label style={styles.compactLabel}>Product Description</label>
              <Select
                isDisabled={editingId && !isEditing}
                options={bomProductDescOptions}
                value={filters.productDescription}
                onChange={(opt) => handleFilterChange("productDescription", opt)}
                placeholder="Product Description..."
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ control: (base) => ({ ...base, minHeight: 32, height: 32, fontSize: 13, borderColor: '#dee2e6' }), menu: (base) => ({ ...base, zIndex: 9999, fontSize: 13 }), menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
            <div className="col-md-4">
              <label style={styles.compactLabel}>Process</label>
              <Select
                isDisabled={editingId && !isEditing}
                options={bomProcessOptions}
                value={filters.process}
                onChange={handleProcessFilterChange}
                placeholder="Process..."
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ control: (base) => ({ ...base, minHeight: 32, height: 32, fontSize: 13, borderColor: '#dee2e6' }), menu: (base) => ({ ...base, zIndex: 9999, fontSize: 13 }), menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: "#16a34a" }}>
            {filteredBomList.length} {filteredBomList.length === 1 ? "product" : "products"} match
          </div>
        </div>

        {/* BOM MAIN TABLE */}
        <div style={{ ...styles.tableContainer, marginTop: 10, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Product Code</th>
                <th style={thStyle}>Process</th>
                <th style={thStyle}>Product Description</th>
                <th style={thStyle}>Base Unit</th>
                <th style={thStyle}>UOM</th>
                <th style={thStyle}>Location</th>
                <th style={thStyle}>BOM Usage</th>
                <th style={thStyle}>Alternative BOM</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {paginatedBomList.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ padding: 20, textAlign: "center", color: "#777" }}>No BOM records found</td>
                </tr>
              ) : (
                paginatedBomList.map((item) => (
                  <tr key={item.bom_id}>
                    <td style={tdStyle}>{mv("product_code", item.product_code)}</td>
                    <td style={tdStyle}>{mv("process_id", item.process_id ? `${item.process_id} - ${item.process_name || ""}` : "")}</td>
                    <td style={tdStyle}>{mv("product_description", item.product_description)}</td>
                    <td style={tdStyle}>{mv("base_unit", formatQty(item.base_unit))}</td>
                    <td style={tdStyle}>{mv("UOM", item.UOM)}</td>
                    <td style={tdStyle}>{mv("location", item.location)}</td>
                    <td style={tdStyle}>{mv("bom_usage", item.bom_usage)}</td>
                    <td style={tdStyle}>{mv("alternative_bom", item.alternative_bom)}</td>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <button
                          className="btn btn-outline-dark btn-sm"
                          style={{ fontSize: "11px", padding: "2px 8px" }}
                          onClick={() => viewDetails(item.bom_id)}
                        >
                          View
                        </button>

                        <button
                          className="btn btn-outline-success btn-sm"
                          style={{ fontSize: "11px", padding: "2px 8px" }}
                          disabled={fs("product_code", "view").masked}
                          onClick={() => openBomTree(item.product_code)}
                        >
                          Tree
                        </button>

                        <button
                          title="Delete"
                          style={{
                            background: "#fff",
                            border: "1px solid #f5c2c7",
                            borderRadius: "4px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "4px 8px",
                            cursor: "pointer"
                          }}
                          onClick={() => handleDeleteBom(item.bom_id)}
                        >
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z"/>
                            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* PAGINATION */}
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginTop: 15, padding: "0 10px 10px 10px", width: "100%" }}>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              style={{ padding: "6px 12px", border: "1px solid #ccc", background: "#fff", cursor: "pointer", borderRadius: 4, fontSize: 16 }}
            >
              {"<"}
            </button>
            <span style={{ fontSize: 13, color: "#334155" }}>Page {currentPage} of {totalPages || 1}</span>
            <button
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage((p) => p + 1)}
              style={{ padding: "6px 12px", border: "1px solid #ccc", background: "#fff", cursor: "pointer", borderRadius: 4, fontSize: 16 }}
            >
              {">"}
            </button>
          </div>
        </div>

        {/* Bulk Excel Upload Modal */}
        {showBulkModal && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1050, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{ background: "white", width: "400px", borderRadius: 12, padding: 25, boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <h4 style={{ margin: 0 }}>Upload BOM</h4>
                <button onClick={() => setShowBulkModal(false)} style={{ background: "transparent", border: "none", fontSize: 18, cursor: "pointer" }}>✖</button>
              </div>
              <div style={{ border: "2px dashed #cbd5e1", borderRadius: 8, padding: 30, textAlign: "center", background: "#f8fafc" }}>
                <p style={{ margin: "0 0 15px 0", fontSize: 14, color: "#475569" }}>Upload your Multi-Level BOM Template</p>
                <input type="file" id="bulkExcelUpload" accept=".xlsx, .xls, .csv" style={{ display: "none" }} onChange={handleBulkExcelUpload} />
                <label htmlFor="bulkExcelUpload" style={{ background: "#03183a", color: "white", padding: "8px 20px", borderRadius: 6, cursor: "pointer", fontWeight: 600, display: "inline-block", marginBottom: 15 }}>Browse File</label>
                <div>
                  <a href="/assets/files/BOM.xlsx" download style={{ fontSize: 13, color: "#03183a", textDecoration: "none", fontWeight: 500 }}>⬇ Download Template</a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Products To Be Maintained Modal */}
        {showMissingModal && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1150, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{ background: "white", width: "460px", maxHeight: "70vh", borderRadius: 12, padding: 20, boxShadow: "0 10px 30px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 15 }}>
                <h4 style={{ margin: 0 }}>Products To Be Maintained in Product Master</h4>
                <button onClick={() => setShowMissingModal(false)} style={{ background: "transparent", border: "none", fontSize: 18, cursor: "pointer" }}>✖</button>
              </div>
              <div style={{ overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Sl No</th>
                      <th style={thStyle}>Product Code</th>
                      <th style={thStyle}>Product Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missingProducts.length === 0 ? (
                      <tr><td colSpan="2" style={{ padding: 20, textAlign: "center", color: "#777" }}>No pending products</td></tr>
                    ) : (
                        missingProducts.map((p, idx) => (
                          <tr key={p.id}>
                            <td style={tdStyle}>{idx + 1}</td>
                            <td style={tdStyle}>{p.product_code}</td>
                            <td style={tdStyle}>{p.product_description}</td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* MASTER PREVIEW TABLE */}
        {showMasterPreview && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1060, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{ background: "white", width: "85vw", height: "85vh", borderRadius: 12, padding: 20, display: "flex", flexDirection: "column", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #eee", paddingBottom: 15, marginBottom: 15 }}>
                <h3 style={{ margin: 0, color: "#1e293b" }}>👀 Preview Uploaded BOMs</h3>
                <button onClick={() => setShowMasterPreview(false)} style={{ background: "#f1f5f9", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Close</button>
              </div>

              <div style={{ flex: 1, overflowY: "auto", paddingRight: 10 }}>
                {draftsList.length === 0 ? (
                  <p style={{ textAlign: 'center', marginTop: 40, color: '#666' }}>No Pending BOMs found.</p>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ background: "#f1f1f1" }}>
                        <th style={thStyle}>Product Code</th>
                        <th style={thStyle}>Base Unit</th>
                        <th style={thStyle}>Location</th>
                        <th style={thStyle}>BOM Usage</th>
                        <th style={thStyle}>Alternative BOM</th>
                        <th style={thStyle}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draftsList.map((bom, idx) => (
                        <tr key={idx}>
                          <td style={tdStyle}>{bom.parentCode}</td>
                          <td style={tdStyle}>{bom.headerDetails.baseUnit}</td>
                          <td style={tdStyle}>{bom.headerDetails.location}</td>
                          <td style={tdStyle}>{bom.headerDetails.bomUsage}</td>
                          <td style={tdStyle}>{bom.headerDetails.alternativeBom}</td>
                          <td style={tdStyle}>
                            <button onClick={() => viewPreviewDetails(bom)} style={{ padding: "6px 10px", background: "#1E88E5", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, marginRight: 8 }}>View</button>
                            <button
                              onClick={() => { if (!canPublish) return toast.error("You don't have Create access."); onClickPublishDraft(bom.parentCode); }}
                              disabled={publishingId === bom.parentCode || isPublishingAll}
                              style={{ padding: "6px 10px", background: "#10b981", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, marginRight: 8 }}
                            >
                              {publishingId === bom.parentCode ? "Saving..." : "Save"}
                            </button>
                            <button
                              title="Delete draft"
                              onClick={() => handleDeleteDraft(bom.parentCode)}
                              disabled={deletingDraftCode === bom.parentCode}
                              style={{
                                background: "#fff",
                                border: "1px solid #f5c2c7",
                                borderRadius: "4px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "4px 8px",
                                cursor: deletingDraftCode === bom.parentCode ? "wait" : "pointer"
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
                                <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {draftsList.length > 0 && (
                <div style={{ borderTop: "1px solid #eee", paddingTop: 15, marginTop: 15, display: "flex", justifyContent: "flex-end" }}>
                  <button
                    onClick={() => { if (!canPublish) return toast.error("You don't have Create access."); handlePublishAllDrafts(); }}
                    disabled={isPublishingAll}
                    style={{ background: "#10b981", color: "white", border: "none", padding: "10px 24px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 15 }}
                  >
                    {isPublishingAll ? "Saving All..." : "Save All BOMs"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* FLOATING BADGE */}
        <div style={{ position: 'fixed', bottom: 30, right: 30, display: 'flex', flexDirection: 'column', gap: 15, zIndex: 9999 }}>
          {draftsCount > 0 && (
            <div onClick={() => setShowMasterPreview(true)} style={{ background: '#1e293b', color: '#fff', padding: '12px 20px', borderRadius: 50, cursor: 'pointer', boxShadow: '0 8px 20px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 'bold', fontSize: 15 }}>📂 Pending Drafts</span>
              <span style={{ background: '#ef4444', color: 'white', padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 'bold' }}>{draftsCount}</span>
            </div>
          )}
        </div>

        {/* EXISTING OLD MODAL */}
        {showModal && (
          <>
            <div onClick={closeViewModal} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100 }} />
            <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "88vw", height: "84vh", overflowY: "auto", overflowX: "hidden", background: "white", zIndex: 1101, borderRadius: 12, padding: "16px 20px", boxShadow: "0 10px 40px rgba(0,0,0,0.25)", fontSize: "12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{editingId === "preview" ? "Preview BOM Details" : (editingId ? "BOM" : "Create BOM")}</h3>
                <button onClick={closeViewModal} aria-label="Close" style={{ background: "#f1f5f9", border: "none", width: 28, height: 28, borderRadius: "50%", cursor: "pointer", fontSize: 14, color: "#475569", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
              </div>
              <hr style={{ marginTop: 10, marginBottom: 14, border: "none", borderTop: "1px solid #e5e7eb" }} />

              <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 12px 4px", marginBottom: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px", alignItems: "end" }}>
                <FieldBox st={fs("product_code")} label="Product Code">
                  <Select
                    isDisabled={editingId && !isEditing}
                    options={products.map((p) => ({ value: p.productCode, label: p.productCode }))}
                    value={form.product_code ? { value: form.product_code, label: form.product_code } : null}
                    onChange={(selectedOption) => {
                      const code = selectedOption ? selectedOption.value : "";
                      const selectedProduct = products.find((p) => p.productCode === code);
                      setForm((f) => ({ ...f, product_code: code, product_description: selectedProduct?.productDescription || "", UOM: selectedProduct?.uom || "" }));
                    }}
                    placeholder="Select Product" isClearable
                    styles={{ control: (base) => ({ ...base, minHeight: "30px", height: "30px", fontSize: "12px", borderRadius: "6px", borderColor: "#cbd5e1" }), valueContainer: (base) => ({ ...base, padding: "0 8px", height: "30px" }), indicatorsContainer: (base) => ({ ...base, height: "30px" }), menu: (base) => ({ ...base, zIndex: 9999, fontSize: "12px" }) }}
                  />
                </FieldBox>
                <FieldBox st={fs("process_id")} label="Process">
                  <Select
                    isDisabled={editingId && !isEditing}
                    options={processList.map((p) => ({ value: p.process_id, label: `${p.process_id} - ${p.process_name}` }))}
                    value={form.process_id ? { value: form.process_id, label: `${form.process_id} - ${form.process_name || ""}` } : null}
                    onChange={(selectedOption) => {
                      const proc = processList.find((p) => p.process_id === selectedOption?.value);
                      setForm((f) => ({
                        ...f,
                        process_id: selectedOption ? selectedOption.value : "",
                        process_name: proc?.process_name || "",
                      }));
                    }}
                    placeholder="Select Process" isClearable
                    styles={{ control: (base) => ({ ...base, minHeight: "30px", height: "30px", fontSize: "12px", borderRadius: "6px", borderColor: "#cbd5e1" }), valueContainer: (base) => ({ ...base, padding: "0 8px", height: "30px" }), indicatorsContainer: (base) => ({ ...base, height: "30px" }), menu: (base) => ({ ...base, zIndex: 9999, fontSize: "12px" }) }}
                  />
                </FieldBox>
                <FieldBox st={fs("product_description")} label="Product Description">
                  <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <input value={form.product_description || ""} readOnly title={form.product_description || ""} style={{ ...smallInput, flex: 1, background: "#f5f5f5", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} />
                    <span title={form.product_description || ""} style={{ width: "18px", height: "18px", minWidth: "18px", borderRadius: "50%", background: "#021843", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", cursor: "pointer" }}>i</span>
                  </div>
                </FieldBox>
                <FieldBox st={fs("base_unit")} label="Base Unit">
                  <input type="number" disabled={editingId && !isEditing} style={{ ...smallInput, width: "100%" }} value={form.base_unit} onChange={(e) => setForm((f) => ({ ...f, base_unit: e.target.value }))} />
                </FieldBox>
                <FieldBox st={fs("UOM")} label="UOM">
                  <input value={form.UOM || ""} readOnly style={{ ...smallInput, width: "100%", background: "#f5f5f5" }} />
                </FieldBox>
                <FieldBox st={fs("location")} label="Location">
                  <select disabled={editingId && !isEditing} style={{ ...smallInput, width: "100%" }} value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}>
                    <option value="">Select</option>
                    {locations.map((loc) => (<option key={loc.id} value={loc.name}>{loc.name}</option>))}
                  </select>
                </FieldBox>
                <FieldBox st={fs("bom_usage")} label="BOM Usage">
                  <select disabled={editingId && !isEditing} style={{ ...smallInput, width: "100%" }} value={form.bom_usage} onChange={(e) => setForm((f) => ({ ...f, bom_usage: e.target.value }))}>
                    <option value="">Select</option>
                    <option value="Production">Production</option>
                  </select>
                </FieldBox>
                <FieldBox st={fs("alternative_bom")} label="Alternative BOM">
                  <input disabled={editingId && !isEditing} style={{ ...smallInput, width: "100%" }} value={form.alternative_bom} onChange={(e) => setForm((f) => ({ ...f, alternative_bom: e.target.value }))} />
                </FieldBox>

                {!editingId && (
                  <div>
                    <label style={smallLabel}>Reference BOM</label>
                    <select
                      style={{ ...smallInput, width: "100%" }} value={referenceBom}
                      onChange={async (e) => {
                        const bomId = e.target.value;
                        setReferenceBom(bomId);
                        if (!bomId) return;
                        try {
                          const res = await axios.get(`${MASTER_API}/bom/reference/components/${bomId}`);
                          const rows = res.data.data || [];
                          const mapped = rows.map((c) => ({
                            level: c.level || "",
                            item: c.item || "",
                            product_type: c.product_type || "",
                            component_product: c.component_product || "",
                            component_description: c.component_description || "",
                            quantity: c.quantity ? formatQty(c.quantity) : "",
                            uom_component: c.uom_component || "",
                            assembly: c.assembly || "",
                            valid_from: c.valid_from ? c.valid_from.split("T")[0] : "",
                            valid_to: c.valid_to ? c.valid_to.split("T")[0] : ""
                          }));
                          setComponentRows(mapped);

                          // Copy header fields from the reference BOM too
                          const header = rows[0];
                          if (header) {
                            const selectedProduct = products.find((p) => p.productCode === header.product_code);
                            setForm((f) => ({
                              ...f,
                              product_code: header.product_code || "",
                              process_id: header.process_id || "",
                              process_name: header.process_name || "",
                              product_description: header.product_description || selectedProduct?.productDescription || "",
                              base_unit: header.base_unit || "",
                              UOM: header.UOM || selectedProduct?.uom || "",
                              location: header.location || "",
                              bom_usage: header.bom_usage || "",
                              alternative_bom: header.alternative_bom || ""
                            }));
                          }
                        } catch { toast.error("Failed to load Reference BOM"); }
                      }}
                    >
                      <option value="">Select</option>
                      {referenceList.map((r) => (<option key={r.bom_id} value={r.bom_id}>{r.product_code}</option>))}
                    </select>
                  </div>
                )}
              </div>
              </div>

              <div style={{ display: "flex", gap: 6, marginBottom: 18, background: "#f1f5f9", padding: 5, borderRadius: 10, width: "fit-content" }}>
                <button onClick={() => setActiveTab("component")} style={{ padding: "8px 18px", border: "none", borderRadius: 8, background: activeTab === "component" ? "#0f172a" : "transparent", color: activeTab === "component" ? "#fff" : "#334155", cursor: "pointer", fontWeight: 600, fontSize: 13, transition: "none" }}>Component</button>
                <button onClick={() => setActiveTab("document")} style={{ padding: "8px 18px", border: "none", borderRadius: 8, background: activeTab === "document" ? "#0f172a" : "transparent", color: activeTab === "document" ? "#fff" : "#334155", cursor: "pointer", fontWeight: 600, fontSize: 13, transition: "none" }}>Documents</button>
                <button onClick={() => setActiveTab("changeLog")} style={{ padding: "8px 18px", border: "none", borderRadius: 8, background: activeTab === "changeLog" ? "#0f172a" : "transparent", color: activeTab === "changeLog" ? "#fff" : "#334155", cursor: "pointer", fontWeight: 600, fontSize: 13, transition: "none" }}>Change Log</button>
              </div>

              {activeTab === "component" && (
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                    <button disabled={editingId && !isEditing} onClick={addComponentRow} style={{ padding: "6px 10px", borderRadius: 6, border: "none", cursor: "pointer", background: "#1976d2", color: "white", fontSize: 13 }}>+ Add Row</button>
                  </div>
                  <div style={{ width: "100%", overflowX: "auto", overflowY: "visible" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto", fontSize: 13, minWidth: "1250px" }}>
                      <colgroup>
                        <col style={{ width: "8%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "20%" }} />
                        <col style={{ width: "8%" }} />
                        <col style={{ width: "7%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "10%" }} />
                      </colgroup>
                      <thead>
                        <tr style={{ background: "#fafafa" }}>
                          {!fs("item").hidden && <th style={thStyle}>Item</th>}
                          {!fs("product_type").hidden && <th style={thStyle}>Product Type</th>}
                          {!fs("component_product").hidden && <th style={thStyle}>Product Code</th>}
                          {!fs("component_description").hidden && <th style={thStyle}>Product Description</th>}
                          {!fs("quantity").hidden && <th style={thStyle}>Qty</th>}
                          {!fs("uom_component").hidden && <th style={thStyle}>UOM</th>}
                          {!fs("assembly").hidden && <th style={thStyle}>Assembly</th>}
                          {!fs("valid_from").hidden && <th style={thStyle}>Valid From</th>}
                          {!fs("valid_to").hidden && <th style={thStyle}>Valid To</th>}
                          <th style={thStyle}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {componentRows.map((row, idx) => (
                          <tr key={idx}>
                            {fs("item").hidden ? null : (<td style={tdStyle}>{cf("item", (<><input disabled={editingId && !isEditing} value={row.item} onChange={(e) => updateComponentRow(idx, "item", e.target.value)} style={{ ...smallInput, width: "100%" }} /></>))}</td>)}
                            {fs("product_type").hidden ? null : (<td style={tdStyle}>{cf("product_type", (<><input value={row.product_type || ""} readOnly style={{ ...smallInput, width: "100%", background: "#f5f5f5" }} /></>))}</td>)}
                            {fs("component_product").hidden ? null : (<td style={{ ...tdStyle, minWidth: "220px" }}>{cf("component_product", (<>
                              <Select
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                isSearchable
                                placeholder="Select Product"
                                options={products.map((p) => ({ value: p.productCode, label: p.productCode }))}
                                value={row.component_product ? { value: row.component_product, label: row.component_product } : null}
                                onChange={async (selectedOption) => {
                                  const code = selectedOption ? selectedOption.value : "";
                                  const product = products.find((p) => String(p.productCode).trim() === String(code).trim());
                                  updateComponentRow(idx, "component_product", code);
                                  updateComponentRow(idx, "component_description", product?.productDescription || "");
                                  updateComponentRow(idx, "uom_component", product?.uom || "");
                                  updateComponentRow(idx, "product_type", product?.productType || "");
                                  if (code) {
                                    try {
                                      const res = await axios.get(`${MASTER_API}/bom/check-product/${code}`);
                                      updateComponentRow(idx, "assembly", res.data.exists ? "Yes" : "No");
                                    } catch { updateComponentRow(idx, "assembly", "No"); }
                                  } else { updateComponentRow(idx, "assembly", "No"); }
                                }}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 99999 }), menu: (base) => ({ ...base, zIndex: 99999 }), control: (base) => ({ ...base, minHeight: 28, fontSize: 12 }) }}
                              />
                            </>))}</td>)}
                            {fs("component_description").hidden ? null : (<td style={tdStyle}>{cf("component_description", (<>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <input value={row.component_description || ""} readOnly title={row.component_description || ""} style={{ ...smallInput, flex: 1, background: "#f5f5f5", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} />
                                <span title={row.component_description || ""} style={{ width: "18px", height: "18px", borderRadius: "50%", background: "#021843", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", cursor: "pointer", flexShrink: 0 }}>i</span>
                              </div>
                            </>))}</td>)}
                            {fs("quantity").hidden ? null : (<td style={tdStyle}>{cf("quantity", (<><input disabled={editingId && !isEditing} type="number" value={row.quantity} onChange={(e) => updateComponentRow(idx, "quantity", e.target.value)} style={{ ...smallInput, width: "100%" }} /></>))}</td>)}
                            {fs("uom_component").hidden ? null : (<td style={tdStyle}>{cf("uom_component", (<><input value={row.uom_component || ""} readOnly style={{ ...smallInput, width: "100%", background: "#f5f5f5" }} /></>))}</td>)}
                            {fs("assembly").hidden ? null : (<td style={tdStyle}>{cf("assembly", (<>
                              <select disabled={editingId && !isEditing} value={row.assembly} onChange={(e) => updateComponentRow(idx, "assembly", e.target.value)} style={{ ...smallInput, width: "100%" }}>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                              </select>
                            </>))}</td>)}
                            {fs("valid_from").hidden ? null : (<td style={tdStyle}>{cf("valid_from", (<><input disabled={editingId && !isEditing} type="date" value={row.valid_from} onChange={(e) => updateComponentRow(idx, "valid_from", e.target.value)} style={{ ...smallInput, width: "100%" }} /></>))}</td>)}
                            {fs("valid_to").hidden ? null : (<td style={tdStyle}>{cf("valid_to", (<><input disabled={editingId && !isEditing} type="date" value={row.valid_to} onChange={(e) => updateComponentRow(idx, "valid_to", e.target.value)} style={{ ...smallInput, width: "100%" }} /></>))}</td>)}
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <button
                                disabled={editingId && !isEditing}
                                title="Remove"
                                onClick={() => removeComponentRow(idx)}
                                style={{
                                  background: "#fff",
                                  border: "1px solid #f5c2c7",
                                  borderRadius: "4px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: "4px 8px",
                                  cursor: (editingId && !isEditing) ? "not-allowed" : "pointer",
                                  opacity: (editingId && !isEditing) ? 0.5 : 1
                                }}
                              >
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z"/>
                                  <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z"/>
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'document' && (
                <div className="mt-3" style={{ maxWidth: '400px', background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16 }}>
                  <label style={{ ...smallLabel, marginBottom: 8, display: 'block' }}>Attach General Documents</label>
                  <input type="file" multiple disabled={fs("document_path").readOnly || fs("document_path").masked} onChange={handleDocumentUpload} style={{ width: "100%", padding: "6px", border: "1px solid #ccc", borderRadius: "4px", fontSize: "13px" }} />
                  {documents.length > 0 && (
                    <div style={{ marginTop: 15, background: "#f8fafc", padding: 10, borderRadius: 6, border: "1px solid #e2e8f0" }}>
                      <strong style={{ fontSize: 13, color: "#334155" }}>Attached Files:</strong>
                      <ul style={{ margin: "5px 0 0 0", paddingLeft: 20, fontSize: 12, color: "#64748b" }}>
                        {documents.map((d, i) => <li key={i}>{d.name}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "changeLog" && (
                <div style={{ background: "#fff", borderRadius: 10, padding: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                      <thead>
                        <tr style={{ background: "#f8fafc" }}>
                          <th style={thStyle}>Date & Time</th>
                          <th style={thStyle}>User</th>
                          <th style={thStyle}>Action</th>
                          <th style={thStyle}>Status</th>
                          <th style={thStyle}>Field</th>
                          <th style={thStyle}>Old Value</th>
                          <th style={thStyle}>New Value</th>
                          <th style={thStyle}>Comment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bomLogs.length > 0 ? (
                          bomLogs.map((log) => (
                            <tr key={log.id}>
                              <td style={tdStyle}>{log.created_at ? new Date(log.created_at).toLocaleString() : "-"}</td>
                              <td style={tdStyle}>{log.created_by || "Admin"}</td>
                              <td style={tdStyle}>{log.action_type || "-"}</td>
                              <td style={tdStyle}>{log.doc_status || "-"}</td>
                              <td style={tdStyle}>{log.field_name || "-"}</td>
                              <td style={tdStyle}>{log.old_value || "-"}</td>
                              <td style={{ ...tdStyle, fontWeight: 700, color: "#0f172a" }}>{log.new_value || "-"}</td>
                              <td style={tdStyle}>{log.comment || "-"}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan="8" style={{ textAlign: "center", padding: 20, color: "#64748b" }}>No change logs available</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20, borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
                <button onClick={closeViewModal} style={{ width: "100px", height: "38px", fontSize: "13px", fontWeight: 600, borderRadius: "8px", background: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0", cursor: "pointer" }}>Cancel</button>
                {editingId && !isEditing && (
                  <button onClick={() => { if (!canEdit) return toast.error("You don't have Edit access."); setIsEditing(true); }} style={{ ...equalBtnStyle, width: "90px", height: "38px", fontSize: "13px", borderRadius: "8px" }}>Edit</button>
                )}
                {(!editingId || isEditing) && (
                  <button onClick={handleSave} style={{ ...equalBtnStyle, width: "100px", height: "38px", fontSize: "13px", borderRadius: "8px" }}>Save</button>
                )}
              </div>
            </div>
          </>
        )}
      {showTreeModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1200
          }}
          onClick={() => setShowTreeModal(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              width: "80vw",
              height: "85vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 40px rgba(0,0,0,0.25)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "14px 20px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <h3 style={{ margin: 0, fontSize: 16, color: "#1e293b" }}>BOM Tree</h3>
                <span
                  style={{
                    background: "#dbeafe",
                    color: "#1d4ed8",
                    padding: "4px 12px",
                    borderRadius: 20,
                    fontSize: 13,
                    fontWeight: 700
                  }}
                >
                  Product: {bomTree?.code}
                </span>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={expandAllNodes}
                  style={{ background: "#fff", color: "#021843", border: "1px solid #021843", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                >
                  Expand All
                </button>
                <button
                  onClick={collapseAllNodes}
                  style={{ background: "#fff", color: "#021843", border: "1px solid #021843", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                >
                  Collapse All
                </button>
                <button
                  onClick={() => setShowTreeModal(false)}
                  style={{ background: "#d32f2f", color: "white", border: "none", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                >
                  Close
                </button>
              </div>
            </div>

            <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1 }}>
              {bomTree && renderBomNode(bomTree)}
            </div>
          </div>
        </div>
      )}

      {/* ===================== DELETE CONFIRM MODAL ===================== */}
      {showDeleteConfirm && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(0,0,0,0.5)",
          zIndex: 1070,
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}>
          <div style={{
            background: "#fff",
            width: "420px",
            borderRadius: 14,
            padding: "36px 32px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            textAlign: "center",
            position: "relative"
          }}>
            {/* CLOSE */}
            <button
              onClick={() => {
                setShowDeleteConfirm(false);
                setBomToDelete(null);
              }}
              style={{
                position: "absolute",
                top: 16,
                right: 18,
                background: "transparent",
                border: "none",
                fontSize: 18,
                cursor: "pointer",
                color: "#94a3b8"
              }}
            >
              ✖
            </button>

            {/* ICON BADGE */}
            <div style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "#ef4444",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
              fontSize: 28,
              color: "#fff",
              fontWeight: "bold"
            }}>
              🗑
            </div>

            {/* TITLE */}
            <h4 style={{
              margin: "0 0 12px",
              fontSize: "19px",
              fontWeight: 700,
              color: "#1e293b"
            }}>
              Delete BOM
            </h4>

            {/* MESSAGE */}
            <p style={{
              fontSize: 14,
              color: "#64748b",
              marginBottom: 28,
              lineHeight: 1.5
            }}>
              Delete this BOM "{bomToDelete}"? This will hide it from the list.
            </p>

            {/* ACTIONS */}
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: 10
            }}>
              <button
                onClick={confirmDeleteBom}
                style={{
                  padding: "12px 16px",
                  borderRadius: 10,
                  border: "none",
                  background: "#ef4444",
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: 14,
                  width: "100%"
                }}
              >
                Yes, Delete BOM
              </button>

              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setBomToDelete(null);
                }}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "none",
                  background: "transparent",
                  color: "#334155",
                  cursor: "pointer",
                  fontWeight: 500,
                  fontSize: 14,
                  width: "100%"
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

            {/* PER-ROW DRAFT REPLACE CONFIRM (Deleted BOM) */}
      {showDraftReplaceConfirm && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
          background: "rgba(0,0,0,0.5)", zIndex: 1180,
          display: "flex", justifyContent: "center", alignItems: "center"
        }}>
          <div style={{
            background: "#fff", width: "420px", borderRadius: 14,
            padding: "36px 32px", boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            textAlign: "center", position: "relative"
          }}>
            <button
              onClick={() => {
                setShowDraftReplaceConfirm(false);
                setDraftReplaceTarget(null);
              }}
              style={{
                position: "absolute", top: 16, right: 18,
                background: "transparent", border: "none",
                fontSize: 18, cursor: "pointer", color: "#94a3b8"
              }}
            >
              ✖
            </button>
            <h4 style={{ margin: "0 0 12px", fontSize: "19px", fontWeight: 700, color: "#1e293b" }}>
              BOM Already Deleted
            </h4>
            <p style={{ fontSize: 14, color: "#64748b", marginBottom: 28, lineHeight: 1.5 }}>
              BOM for "{draftReplaceTarget}" already exists but is deleted.
              Do you want to replace it with this new data?
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                onClick={() => {
                  const code = draftReplaceTarget;
                  setShowDraftReplaceConfirm(false);
                  handlePublishDraft(code, true);
                }}
                style={{
                  padding: "12px 16px", borderRadius: 10, border: "none",
                  background: "#16a34a", color: "#fff", cursor: "pointer",
                  fontWeight: 600, fontSize: 14, width: "100%"
                }}
              >
                Yes, Replace It
              </button>
              <button
                onClick={() => {
                  setShowDraftReplaceConfirm(false);
                  setDraftReplaceTarget(null);
                }}
                style={{
                  padding: "10px 16px", borderRadius: 10, border: "none",
                  background: "transparent", color: "#334155", cursor: "pointer",
                  fontWeight: 500, fontSize: 14, width: "100%"
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SAVE-ALL BULK REPLACE CONFIRM (Deleted BOMs) */}
      {showBulkReplaceConfirm && (() => {
        const conflictsInList = draftsList
          .map(d => d.parentCode)
          .filter(code => deletedConflictCodes.includes(code));

        return (
          <div style={{
            position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
            background: "rgba(0,0,0,0.5)", zIndex: 1180,
            display: "flex", justifyContent: "center", alignItems: "center"
          }}>
            <div style={{
              background: "#fff", width: "460px", borderRadius: 14,
              padding: "36px 32px", boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              textAlign: "center", position: "relative"
            }}>
              <button
                onClick={() => setShowBulkReplaceConfirm(false)}
                style={{
                  position: "absolute", top: 16, right: 18,
                  background: "transparent", border: "none",
                  fontSize: 18, cursor: "pointer", color: "#94a3b8"
                }}
              >
                ✖
              </button>
              <h4 style={{ margin: "0 0 12px", fontSize: "19px", fontWeight: 700, color: "#1e293b" }}>
                Some BOMs Are Already Deleted
              </h4>
              <p style={{ fontSize: 14, color: "#64748b", marginBottom: 28, lineHeight: 1.5 }}>
                <strong>{conflictsInList.join(", ")}</strong>{" "}
                {conflictsInList.length > 1 ? "already exist" : "already exists"} but{" "}
                {conflictsInList.length > 1 ? "are" : "is"} deleted.
                Do you want to replace {conflictsInList.length > 1 ? "them" : "it"} with the new data?
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button
                  onClick={() => {
                    setShowBulkReplaceConfirm(false);
                    publishAllDraftsApi(true);
                  }}
                  style={{
                    padding: "12px 16px", borderRadius: 10, border: "none",
                    background: "#16a34a", color: "#fff", cursor: "pointer",
                    fontWeight: 600, fontSize: 14, width: "100%"
                  }}
                >
                  Yes, Replace All
                </button>
                <button
                  onClick={() => setShowBulkReplaceConfirm(false)}
                  style={{
                    padding: "10px 16px", borderRadius: 10, border: "none",
                    background: "transparent", color: "#334155", cursor: "pointer",
                    fontWeight: 500, fontSize: 14, width: "100%"
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      </div>
    </Layout>
  );
};

export default BOM;